import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { HelpCircle, Search, X, ArrowLeft, Rocket, ChevronRight } from 'lucide-react'
import {
  HELP_SECTIONS,
  searchHelpResults,
  getHelpSection,
  type HelpSection,
} from '@/config/helpSections'
import {
  HELP_CATEGORIES,
  getVisibleCategories,
  findCategoryForSection,
  getCategorySections,
} from '@/config/helpCategories'
import { useDebounce } from '@/hooks/use-debounce'
import { useUser } from '@/contexts/UserContext'
import HelpSectionWrapper from '@/components/help/HelpSectionWrapper'
import HelpCategoryCard from '@/components/help/HelpCategoryCard'
import HelpSearchResults from '@/components/help/HelpSearchResults'
import GetMoreHelpRail from '@/components/help/GetMoreHelpRail'
import { resolveHelpIcon } from '@/components/help/helpIcons'
import { sectionContentMap } from '@/components/help/helpContentMap'

const SEARCH_SUGGESTIONS = [
  'CRM',
  'Ticketing',
  'Activities',
  'Analytics',
  'Sales',
  'Administration',
]

export default function Help() {
  const location = useLocation()
  const { effectiveUserType } = useUser()

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebounce(searchQuery, 300)

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null)

  const contentHeadingRef = useRef<HTMLHeadingElement>(null)

  const visibleCategories = useMemo(
    () => getVisibleCategories(effectiveUserType),
    [effectiveUserType]
  )
  const searchResults = useMemo(() => searchHelpResults(debouncedQuery), [debouncedQuery])

  const isSearching = debouncedQuery.trim().length > 0

  // Open a category, optionally scrolling to a specific section within it.
  const openCategory = useCallback((categoryId: string, sectionId?: string) => {
    const category = HELP_CATEGORIES.find((c) => c.id === categoryId)
    setSearchQuery('')
    setActiveSectionId(null)
    setActiveCategoryId(categoryId)
    if (category) setExpandedSections(new Set(category.sectionIds))
    setPendingScrollId(sectionId ?? null)
  }, [])

  // Open a section by id — resolves to its category view, or a standalone article
  // for sections that don't belong to a browse category (What's New, Support).
  const openSection = useCallback((sectionId: string, subsectionId?: string) => {
    const category = findCategoryForSection(sectionId)
    setSearchQuery('')
    if (category) {
      setActiveSectionId(null)
      setActiveCategoryId(category.id)
      setExpandedSections(new Set(category.sectionIds))
    } else {
      setActiveCategoryId(null)
      setActiveSectionId(sectionId)
      setExpandedSections(new Set([sectionId]))
    }
    setPendingScrollId(subsectionId ?? sectionId)
  }, [])

  const goBrowse = useCallback(() => {
    setActiveCategoryId(null)
    setActiveSectionId(null)
    setSearchQuery('')
    setPendingScrollId(null)
  }, [])

  const toggleSection = (id: string, open: boolean) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (open) next.add(id)
      else next.delete(id)
      return next
    })
  }

  // Backward-compatible deep links: /help#<sectionId> or /help#<subsectionId>
  useEffect(() => {
    const hash = location.hash.slice(1)
    if (!hash) return
    const section = HELP_SECTIONS.find(
      (s) => s.id === hash || s.subsections?.some((sub) => sub.id === hash)
    )
    if (section) {
      const subId = section.id === hash ? undefined : hash
      openSection(section.id, subId)
    }
  }, [location.hash, openSection])

  // Scroll to the pending target after the content view renders.
  useEffect(() => {
    if (!pendingScrollId) return
    const timer = setTimeout(() => {
      const element = document.getElementById(pendingScrollId)
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setPendingScrollId(null)
    }, 120)
    return () => clearTimeout(timer)
  }, [pendingScrollId, activeCategoryId, activeSectionId])

  // Move focus to the content heading when entering a category/section (a11y),
  // unless we're scrolling to a specific deep-linked target.
  useEffect(() => {
    if ((activeCategoryId || activeSectionId) && !pendingScrollId) {
      contentHeadingRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategoryId, activeSectionId])

  const renderSectionCards = (sections: HelpSection[]) => (
    <div className="space-y-4">
      {sections.map((section) => {
        const Icon = resolveHelpIcon(section.icon)
        const ContentComponent = sectionContentMap[section.id]
        if (!ContentComponent) return null
        return (
          <HelpSectionWrapper
            key={section.id}
            id={section.id}
            icon={Icon}
            title={section.label}
            description={section.description}
            lastUpdated={section.lastUpdated}
            isExpanded={expandedSections.has(section.id)}
            onToggle={(open) => toggleSection(section.id, open)}
          >
            <ContentComponent />
          </HelpSectionWrapper>
        )
      })}
    </div>
  )

  const activeCategory = activeCategoryId
    ? HELP_CATEGORIES.find((c) => c.id === activeCategoryId)
    : null
  const standaloneSection = activeSectionId ? getHelpSection(activeSectionId) : null

  // Determine what to show in the main content column.
  let mainContent: React.ReactNode
  if (isSearching) {
    mainContent = (
      <HelpSearchResults
        query={debouncedQuery}
        results={searchResults}
        onOpenSection={openSection}
        suggestions={SEARCH_SUGGESTIONS}
        onSuggestion={(term) => setSearchQuery(term)}
      />
    )
  } else if (activeCategory) {
    const CategoryIcon = resolveHelpIcon(activeCategory.icon)
    mainContent = (
      <div className="space-y-4">
        <div>
          <Button variant="ghost" size="sm" onClick={goBrowse} className="mb-2 -ml-2">
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
            All help topics
          </Button>
          <div className="flex items-center gap-2">
            <CategoryIcon className="h-6 w-6 text-primary" aria-hidden="true" />
            <h2
              ref={contentHeadingRef}
              tabIndex={-1}
              className="text-2xl font-bold tracking-tight focus-visible:outline-none"
            >
              {activeCategory.label}
            </h2>
          </div>
          <p className="mt-1 text-muted-foreground">{activeCategory.description}</p>
        </div>
        {renderSectionCards(getCategorySections(activeCategory))}
      </div>
    )
  } else if (standaloneSection) {
    const SectionIcon = resolveHelpIcon(standaloneSection.icon)
    mainContent = (
      <div className="space-y-4">
        <div>
          <Button variant="ghost" size="sm" onClick={goBrowse} className="mb-2 -ml-2">
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
            All help topics
          </Button>
          <div className="flex items-center gap-2">
            <SectionIcon className="h-6 w-6 text-primary" aria-hidden="true" />
            <h2
              ref={contentHeadingRef}
              tabIndex={-1}
              className="text-2xl font-bold tracking-tight focus-visible:outline-none"
            >
              {standaloneSection.label}
            </h2>
          </div>
          <p className="mt-1 text-muted-foreground">{standaloneSection.description}</p>
        </div>
        {renderSectionCards([standaloneSection])}
      </div>
    )
  } else {
    // Browse view: featured onboarding callout + role-filtered category grid.
    mainContent = (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => openCategory('getting-started')}
          className="group flex w-full items-center gap-4 rounded-lg border border-primary/30 bg-primary/5 p-4 text-left transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
            <Rocket className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold">New to Grackle CRM? Start here</span>
            <span className="block text-sm text-muted-foreground">
              The essentials, keyboard shortcuts, and your first-day checklist.
            </span>
          </span>
          <ChevronRight
            className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </button>

        <section aria-labelledby="help-categories-heading">
          <h2 id="help-categories-heading" className="sr-only">
            Help categories
          </h2>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2" role="list">
            {visibleCategories.map((category) => (
              <li key={category.id}>
                <HelpCategoryCard category={category} onOpen={openCategory} />
              </li>
            ))}
          </ul>
        </section>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Skip to content link for keyboard users */}
      <a
        href="#help-main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium"
      >
        Skip to help content
      </a>

      {/* Header */}
      <header>
        <div className="flex items-center gap-2">
          <HelpCircle className="h-8 w-8 text-primary" aria-hidden="true" />
          <h1 className="text-3xl font-bold tracking-tight">Help Center</h1>
        </div>
        <p className="mt-2 text-muted-foreground">
          Search for an answer or browse the topics below. Everything you need to manage your
          pipeline, close deals, and get the most out of Grackle CRM.
        </p>
      </header>

      {/* Hero search */}
      <div className="relative max-w-2xl">
        <Search
          className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          placeholder="Search help topics — e.g. “convert a lead”, “quote PDF”, “impersonation”"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-12 pl-11 pr-11 text-base"
          aria-label="Search help topics"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            aria-label="Clear search"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Content + escalation rail */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <main id="help-main-content" className="min-w-0">
          {mainContent}
        </main>
        <aside aria-label="Get more help" className="lg:sticky lg:top-20 lg:self-start">
          <GetMoreHelpRail onOpenSection={openSection} />
        </aside>
      </div>
    </div>
  )
}
